import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Paper,
  Chip,
  CircularProgress,
  Alert,
  Divider,
  TextField,
  IconButton
} from '@mui/material';
import { ArrowBack, Download, Edit, Add, Delete, Save, Cancel } from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { firestore as db } from '../components/firebase.config';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const ViewChecklistPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [checklist, setChecklist] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editedChecklist, setEditedChecklist] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetchChecklist = async () => {
      try {
        const checklistDoc = await getDoc(doc(db, 'auditchecklists', id));
        if (checklistDoc.exists()) {
          const checklistData = { id: checklistDoc.id, ...checklistDoc.data() };
          setChecklist(checklistData);
          setEditedChecklist(JSON.parse(JSON.stringify(checklistData))); // Deep copy
        } else {
          setError('Checklist not found');
        }
      } catch (err) {
        console.error('Error fetching checklist:', err);
        setError('Error loading checklist');
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchChecklist();
    }
  }, [id]);

  const handleEditMode = () => {
    setIsEditing(true);
    setEditedChecklist(JSON.parse(JSON.stringify(checklist))); // Deep copy
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditedChecklist(JSON.parse(JSON.stringify(checklist))); // Reset to original
  };

  const handleSaveChanges = async () => {
    setSaving(true);
    try {
      await updateDoc(doc(db, 'auditchecklists', id), {
        sections: editedChecklist.sections,
        updatedAt: new Date()
      });
      setChecklist(editedChecklist);
      setIsEditing(false);
    } catch (err) {
      console.error('Error saving checklist:', err);
      setError('Error saving changes');
    } finally {
      setSaving(false);
    }
  };

  const addRowToSection = (sectionIndex) => {
    const newEditedChecklist = { ...editedChecklist };
    const newRow = {
      data: {}
    };
    
    // Initialize empty data for each column
    if (editedChecklist.columns) {
      editedChecklist.columns.forEach(column => {
        newRow.data[column.id] = '';
      });
    }
    
    if (!newEditedChecklist.sections[sectionIndex].rows) {
      newEditedChecklist.sections[sectionIndex].rows = [];
    }
    
    newEditedChecklist.sections[sectionIndex].rows.push(newRow);
    setEditedChecklist(newEditedChecklist);
  };

  const deleteRowFromSection = (sectionIndex, rowIndex) => {
    const newEditedChecklist = { ...editedChecklist };
    newEditedChecklist.sections[sectionIndex].rows.splice(rowIndex, 1);
    setEditedChecklist(newEditedChecklist);
  };

  const updateCellData = (sectionIndex, rowIndex, columnId, value) => {
    const newEditedChecklist = { ...editedChecklist };
    if (!newEditedChecklist.sections[sectionIndex].rows[rowIndex].data) {
      newEditedChecklist.sections[sectionIndex].rows[rowIndex].data = {};
    }
    newEditedChecklist.sections[sectionIndex].rows[rowIndex].data[columnId] = value;
    setEditedChecklist(newEditedChecklist);
  };

  const generatePDF = async (checklist) => {
    try {
      const pdfDoc = new jsPDF({ orientation: 'landscape' });
      
      // Set headers based on the columns
      const headers = checklist.columns?.map((col) => col.name) || [];
      
      // Handle both sections and rows format
      let rows = [];
      
      if (checklist.sections) {
        // New sections format
        checklist.sections.forEach(section => {
          // Add section header row
          rows.push([section.type, section.title, '', '', '', '', '', '', '']);
          
          // Add section data rows
          section.rows?.forEach(row => {
            const rowData = checklist.columns?.map((col) => {
              const cellData = row.data?.[col.id];
              if (col.name === "Attachments" && cellData && typeof cellData === 'object') {
                return Object.values(cellData)
                  .filter(url => typeof url === 'string' && url.startsWith('http'))
                  .join(', ');
              }
              if (typeof cellData === 'object' && cellData !== null) {
                const urls = Object.entries(cellData)
                  .filter(([key, value]) => key.includes('Url') && value)
                  .map(([key, value]) => key.replace('Url', ''))
                  .join(', ');
                return urls || '';
              }
              return cellData || "";
            }) || [];
            rows.push(rowData);
          });
        });
      } else if (checklist.rows) {
        // Old rows format (fallback)
        rows = checklist.rows?.map((row) => {
          const rowData = checklist.columns?.map((col) => {
            const cellData = row.data?.[col.id];
            if (typeof cellData === 'object' && cellData !== null) {
              const urls = Object.entries(cellData)
                .filter(([key, value]) => key.includes('Url') && value)
                .map(([key, value]) => key.replace('Url', ''))
                .join(', ');
              return urls || '';
            }
            return cellData || "";
          }) || [];
          return rowData;
        }) || [];
      }
      
      // Add the checklist name as the title
      pdfDoc.setFontSize(16);
      pdfDoc.text(checklist.name || "Untitled Checklist", 10, 15);
      
      // Add description if available
      let startY = 25;
      if (checklist.description) {
        pdfDoc.setFontSize(10);
        pdfDoc.text(`Description: ${checklist.description}`, 10, startY);
        startY += 10;
      }
      
      // Create the table using autoTable function
      autoTable(pdfDoc, {
        head: headers.length ? [headers] : [],
        body: rows,
        startY: startY,
        theme: "grid",
        styles: {
          fontSize: 8,
          cellPadding: 2,
          overflow: 'linebreak',
        },
        headStyles: {
          fillColor: [41, 128, 185],
          textColor: 255,
          fontStyle: 'bold',
        },
      });
      
      // Save the PDF
      pdfDoc.save(`${checklist.name || 'checklist'}.pdf`);
    } catch (error) {
      console.error('Error generating PDF:', error);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    try {
      return new Date(timestamp.seconds * 1000).toLocaleDateString();
    } catch {
      return new Date(timestamp).toLocaleDateString();
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/admin')}
          variant="outlined"
        >
          Back to Admin
        </Button>
      </Container>
    );
  }

  if (!checklist) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="warning" sx={{ mb: 2 }}>
          Checklist not found
        </Alert>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/admin')}
          variant="outlined"
        >
          Back to Admin
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Button
            startIcon={<ArrowBack />}
            onClick={() => navigate('/admin')}
            variant="outlined"
            sx={{ mb: 2 }}
          >
            Back to Admin
          </Button>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {!isEditing ? (
              <>
                <Button
                  startIcon={<Edit />}
                  onClick={handleEditMode}
                  variant="outlined"
                  color="secondary"
                >
                  Edit Mode
                </Button>
                <Button
                  startIcon={<Edit />}
                  onClick={() => navigate(`/edit-checklist/${checklist.id}`)}
                  variant="outlined"
                  color="secondary"
                >
                  Full Edit
                </Button>
                <Button
                  startIcon={<Download />}
                  onClick={() => generatePDF(checklist)}
                  variant="contained"
                  color="primary"
                >
                  Download PDF
                </Button>
              </>
            ) : (
              <>
                <Button
                  startIcon={<Save />}
                  onClick={handleSaveChanges}
                  variant="contained"
                  color="primary"
                  disabled={saving}
                >
                  {saving ? 'Saving...' : 'Save Changes'}
                </Button>
                <Button
                  startIcon={<Cancel />}
                  onClick={handleCancelEdit}
                  variant="outlined"
                  color="secondary"
                >
                  Cancel
                </Button>
              </>
            )}
          </Box>
        </Box>
        
        <Typography variant="h4" component="h1" gutterBottom>
          {checklist.name || 'Untitled Checklist'}
        </Typography>
        
        {checklist.description && (
          <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
            {checklist.description}
          </Typography>
        )}
        
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Chip
            label={`Status: ${checklist.status || 'Active'}`}
            color={checklist.status === 'Active' ? 'success' : 'default'}
          />
          <Chip
            label={`Created: ${formatDate(checklist.createdAt)}`}
            variant="outlined"
          />
          <Chip
            label={`Sections: ${checklist.sections?.length || 0}`}
            variant="outlined"
          />
        </Box>
      </Box>

      <Divider sx={{ mb: 4 }} />

      {/* Checklist Content */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Checklist Structure
          </Typography>
          
          {checklist.columns && checklist.columns.length > 0 && (
            <Box sx={{ mb: 4 }}>
              <Typography variant="subtitle1" gutterBottom>
                Columns:
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {checklist.columns.map((column, index) => (
                  <Chip key={index} label={column.name} variant="outlined" size="small" />
                ))}
              </Box>
            </Box>
          )}
          
          {(isEditing ? editedChecklist : checklist).sections && (isEditing ? editedChecklist : checklist).sections.length > 0 ? (
            <Box>
              <Typography variant="subtitle1" gutterBottom>
                Sections and Data:
              </Typography>
              {(isEditing ? editedChecklist : checklist).sections.map((section, sectionIndex) => (
                <Card key={sectionIndex} variant="outlined" sx={{ mb: 3 }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Typography variant="h6" sx={{ backgroundColor: '#fff3cd', p: 1, borderRadius: 1, flexGrow: 1 }}>
                        {section.type && section.title ? `${section.type}. ${section.title}` : section.title || section.type || `Section ${sectionIndex + 1}`}
                      </Typography>
                      {isEditing && (
                        <Button
                          startIcon={<Add />}
                          onClick={() => addRowToSection(sectionIndex)}
                          variant="outlined"
                          size="small"
                          sx={{ ml: 2 }}
                        >
                          Add Row
                        </Button>
                      )}
                    </Box>
                    
                    {section.rows && section.rows.length > 0 ? (
                      <TableContainer component={Paper} variant="outlined">
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              {(isEditing ? editedChecklist : checklist).columns?.map((column, colIndex) => (
                                <TableCell key={colIndex}>
                                  <strong>{column.name}</strong>
                                </TableCell>
                              ))}
                              {isEditing && (
                                <TableCell>
                                  <strong>Actions</strong>
                                </TableCell>
                              )}
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {section.rows.map((row, rowIndex) => (
                              <TableRow key={rowIndex}>
                                {(isEditing ? editedChecklist : checklist).columns?.map((column, colIndex) => {
                                  const cellData = row.data?.[column.id];
                                  let displayValue = '';
                                  
                                  if (!isEditing) {
                                    if (column.name === "Attachments" && cellData && typeof cellData === 'object') {
                                      displayValue = Object.values(cellData)
                                        .filter(url => typeof url === 'string' && url.startsWith('http'))
                                        .map((url, urlIndex) => (
                                          <a key={urlIndex} href={url} target="_blank" rel="noopener noreferrer" style={{ display: 'block', marginBottom: '4px' }}>
                                            Attachment {urlIndex + 1}
                                          </a>
                                        ));
                                    } else if (typeof cellData === 'object' && cellData !== null) {
                                      const urls = Object.entries(cellData)
                                        .filter(([key, value]) => key.includes('Url') && value)
                                        .map(([key, value]) => key.replace('Url', ''))
                                        .join(', ');
                                      displayValue = urls || '';
                                    } else {
                                      displayValue = cellData || '';
                                    }
                                  }
                                  
                                  return (
                                    <TableCell key={colIndex}>
                                      {isEditing ? (
                                        <TextField
                                          value={cellData || ''}
                                          onChange={(e) => updateCellData(sectionIndex, rowIndex, column.id, e.target.value)}
                                          variant="outlined"
                                          size="small"
                                          fullWidth
                                          multiline={column.name === 'Guideline prompts' || column.name === 'Observation'}
                                          rows={column.name === 'Guideline prompts' || column.name === 'Observation' ? 2 : 1}
                                        />
                                      ) : (
                                        displayValue
                                      )}
                                    </TableCell>
                                  );
                                })}
                                {isEditing && (
                                  <TableCell>
                                    <IconButton
                                      onClick={() => deleteRowFromSection(sectionIndex, rowIndex)}
                                      color="error"
                                      size="small"
                                    >
                                      <Delete />
                                    </IconButton>
                                  </TableCell>
                                )}
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        No data in this section
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              ))}
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No sections defined in this checklist
            </Typography>
          )}
        </CardContent>
      </Card>
    </Container>
  );
};

export default ViewChecklistPage;